const { playerPath, parsePlayerLink, canonicalPlayerLink } = require('./playerRoutes');
const secureStream = require('./secureStream');

const selections = new Map();
const listeners = new Set();
const MAX_AGE_MS = 30 * 60 * 1000;
const MAX_ENTRIES = 100;
const STORAGE_KEY = 'caroline.player.sources.v1';

const subscribeSelection = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
};
const getPlayerSelection = (path) => {
    const entry = selections.get(path);
    return entry && Date.now() - entry.createdAt < MAX_AGE_MS ? entry.selection : null;
};
const preparePlayerNavigation = (link) => {
    const selection = parsePlayerLink(link);
    if (selection && !selection.canonical) {
        const path = playerPath(selection);
        selections.delete(path);
        selections.set(path, { selection, createdAt: Date.now() });
        while (selections.size > MAX_ENTRIES) selections.delete(selections.keys().next().value);
        listeners.forEach((listener) => listener());
    }
    return canonicalPlayerLink(link);
};

// Persist only a SHA-256 fingerprint, never a URL, manifest, token or payload.
// An expired URL that changes identity intentionally requires source selection.
const sourceFingerprint = async (selection, stream) => {
    const normalized = secureStream(stream);
    const identity = normalized?.infoHash ? ['torrent', normalized.infoHash, normalized.fileIdx ?? null]
        : normalized?.url ? ['url', normalized.url]
            : normalized?.ytId ? ['youtube', normalized.ytId] : null;
    if (!identity || !globalThis.crypto?.subtle) return null;
    try {
        const bytes = new TextEncoder().encode(JSON.stringify([selection.streamTransportUrl || '', identity]));
        const hash = await globalThis.crypto.subtle.digest('SHA-256', bytes);
        return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, '0')).join('');
    } catch (_) { return null; }
};
const readFingerprints = () => {
    try {
        const data = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]');
        return Array.isArray(data) ? data.filter((entry) => entry && typeof entry.path === 'string' &&
            /^\/player\//.test(entry.path) && /^[a-f0-9]{64}$/.test(entry.fingerprint) &&
            Number.isFinite(entry.createdAt) && Date.now() - entry.createdAt >= 0 && Date.now() - entry.createdAt < MAX_AGE_MS).slice(-MAX_ENTRIES).map(({ path, fingerprint, createdAt }) => ({ path, fingerprint, createdAt })) : [];
    } catch (_) { return []; }
};
const rememberSource = async (selection, stream) => {
    const path = playerPath(selection);
    const fingerprint = await sourceFingerprint(selection, stream);
    // Ignore hashing work superseded by a newer source/navigation.
    if (!fingerprint || !path || getPlayerSelection(path) !== selection) return;
    const entries = readFingerprints().filter((entry) => entry.path !== path);
    entries.push({ path, fingerprint, createdAt: Date.now() });
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES))); } catch (_) { /* Storage is optional. */ }
};
const findRememberedSource = async (path, streams, decodeStream) => {
    const remembered = readFingerprints().find((entry) => entry.path === path);
    if (!remembered) return null;
    for (const stream of streams) {
        const selection = parsePlayerLink(stream.deepLinks?.player);
        if (!selection || selection.canonical || playerPath(selection) !== path) continue;
        try {
            const fingerprint = await sourceFingerprint(selection, stream);
            const candidate = fingerprint || await sourceFingerprint(selection, await decodeStream(selection.stream));
            if (candidate === remembered.fingerprint) return stream.deepLinks.player;
        } catch (_) { /* A malformed/expired candidate cannot prevent selection of another source. */ }
    }
    return null;
};

module.exports = { subscribeSelection, getPlayerSelection, preparePlayerNavigation, rememberSource, findRememberedSource, sourceFingerprint };
